import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import { useHasActiveTikTokOffer } from '../../hooks/reactQuery/useHasActiveTikTokOffer';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import Button from '../Button/Button';
import { TikTokIcon } from '../Icons';

const CONTENT_TOP = 224;
const BG_COLOR = '#1a1a1a';

export const TikTokOfferBanner: React.FC = () => {
  const venue = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online) || false;

  const { hasActiveTikTokOffer, isLoading } = useHasActiveTikTokOffer();

  if (isLoading || hasActiveTikTokOffer !== false) return null;

  const createPath = isOnlineBrand ? '/products/create' : '/offers/create';
  const bannerVideo = `${process.env.PUBLIC_URL}/videos/${
    isOnlineBrand ? 'tiktok-banner-brand' : 'tiktok-banner-venue'
  }.mp4`;

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 416 }}>
      {/* Animated TikTok-colored glow travelling around the outside of the card */}
      <Box
        sx={{
          position: 'absolute',
          inset: '-3px',
          zIndex: 0,
          borderRadius: '19px',
          overflow: 'hidden',
          pointerEvents: 'none',
          filter: 'blur(5px)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 700,
            height: 700,
            transform: 'translate(-50%, -50%)',
            background:
              'conic-gradient(from 0deg, transparent 0%, transparent 58%, #00F7EF 72%, #FF004F 86%, transparent 100%)',
            animation: 'tiktokBorderSpin 4s linear infinite',
            '@keyframes tiktokBorderSpin': {
              to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          borderRadius: '16px',
          backgroundColor: BG_COLOR,
          width: '100%',
          // TikTok-style colored glows (above the video, below the content)
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 1,
            top: '-35%',
            left: '-30%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(0, 247, 239, 0.45) 0%, rgba(0, 247, 239, 0) 70%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 1,
            bottom: '-35%',
            right: '-30%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255, 0, 79, 0.4) 0%, rgba(255, 0, 79, 0) 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          component='video'
          src={bannerVideo}
          autoPlay
          loop
          muted
          playsInline
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        {/* Gradually fade the video into the dark background toward the bottom */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: `linear-gradient(to bottom, rgba(26,26,26,0) 0%, rgba(26,26,26,0.3) 25%, rgba(26,26,26,0.78) 45%, rgba(26,26,26,0.97) 62%, ${BG_COLOR} 78%)`,
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 3,
            px: 4,
            pb: 4,
            pt: `${CONTENT_TOP}px`,
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 56,
              height: 56,
              borderRadius: '14px',
              backgroundColor: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TikTokIcon sx={{ fontSize: '30px' }} />
          </Box>

          <Box>
            <Typography sx={{ color: '#ffffff', fontSize: '20px', fontWeight: 600, mb: 0.5 }}>
              Create your first TikTok offer
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '15px' }}>
              {isOnlineBrand
                ? 'Models create TikTok posts about your brand in exchange for your product.'
                : 'Models create TikTok posts about your venue in exchange for your product or service.'}
            </Typography>
          </Box>

          <Button
            component={Link}
            to={createPath}
            variant='contained'
            fullWidth
            sx={{
              mt: 1,
              fontSize: '16px',
              fontWeight: 500,
              px: 3,
              py: 1.3,
              backgroundColor: '#ffffff',
              color: '#000000',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
            startIcon={<TikTokIcon sx={{ fontSize: '18px' }} />}
          >
            {isOnlineBrand ? 'Create TikTok offer' : 'Create a TikTok offer'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TikTokOfferBanner;
